<template>
  <span class="inspection-timeline-activity-content">
    <!-- causer -->
    <template v-if="shouldDisplayCauser">
      <span v-if="!activity.causer">Platform</span>
      <span v-else-if="activity.causer.business">{{ activity.causer.full_name }} ({{ activity.causer.business.name || '#'+activity.causer.business.id }})</span>
      <span v-else-if="activity.causer.role === 'agent'">
        Agent <router-link :to="{ name: 'app.agents', params: { id: activity.causer.id }}">{{ activity.causer.full_name }}</router-link>
      </span>
      <span v-else>{{ activity.causer.first_name }}</span>
    </template>

    <!--  comments  -->
    <template v-if="activity.type === $config.inspectionActivityTypes.COMMENT">
      : <strong>"{{ activity.comment }}"</strong>
    </template>
    <!-- tenant scheduling mail sent -->
    <template v-else-if="activity.type === $config.inspectionActivityTypes.TENANT_SCHEDULING_MAIL_SENT">
      <span>Tenant scheduling request email sent.</span>
    </template>
    <!-- scheduled by tenant -->
    <template v-else-if="isScheduledByTenant">
      <span>Schedule chosen by tenant:</span>
      <strong>
        {{ formatDateValue(newValues.preferred_from, newValues.preferred_until) }}
      </strong>
    </template>
    <template v-else-if="activity.type === $config.inspectionActivityTypes.AUDIT">
      <!-- Tenant Audits   -->
      <template v-if="oldValues.tenant_scheduling_reminders || newValues.tenant_scheduling_reminders">
        {{ momentUtil.localeData().ordinal(newValues.tenant_scheduling_reminders.length) }} reminder to schedule sent to tenants (email & text)
      </template>
      <template v-if="oldValues.tenant || newValues.tenant">
        <template v-if="oldValues.tenant === null">
          added new tenant: <strong>{{ newValues.tenant.full_name }}</strong>
        </template>
        <template v-else-if="newValues.tenant === null">
          removed tenant: <strong>{{ oldValues.tenant.full_name }}</strong>
        </template>
        <template v-else>
          updated tenant #{{ oldValues.tenant.id }}:
          <span v-for="(key, index) in Object.keys(oldValues.tenant).filter((key)=> key !== 'id')">
            <span v-html="resolveValueChangeText(`tenant.${key}`)" />
          </span>
        </template>
      </template>

      <!-- Inspection Audits  -->
      <template v-else-if="oldValues.state">
        changed status from <strong>{{ oldValues.state }}</strong> to <strong>{{ newValues.state }}</strong>
      </template>
      <template v-else-if="hasField('id')">
        {{ oldValues.id === null? 'ordered' : 'cancelled' }} report in {{ inspection.property.short_address }}
      </template>
      <template v-else-if="hasField('agent_id')">
        {{ oldValues.agent_id === null ? 'assigned' : 'unassigned' }}
        agent #{{ oldValues.agent_id || newValues.agent_id }}
        <strong>{{ oldValues.full_name || newValues.full_name }}</strong>
      </template>
      <template v-else-if="hasField('tenant_notified_at')">
        {{ oldValues.tenant_notified_at === null ? 'checked' : 'unchecked' }} tenants notifications
      </template>
      <template v-else-if="hasField('brief')">
        sent briefing about this inspection to agent: <strong>{{ newValues.brief }}</strong>
      </template>
      <template v-else-if="hasField('report_url')">
        updated the report file from: {{ oldValues.report_url }} to: {{ newValues.report_url }}
      </template>
      <span v-else-if="hasField('tenant_notice_sent_at')">
        sent email reminders to tenants about the upcoming inspection.
        <div v-if="activity.display_data && activity.display_data.tenants">
          <strong>Tenants: </strong>
          {{ activity.display_data.tenants.map(tenant => `${tenant.full_name} (${tenant.email})`).join(', ') }}
        </div>
      </span>
      <span v-else-if="hasField('tenant_reminder_sent_at')">
        sent SMS reminders to tenants about the upcoming inspection.
        <div v-if="activity.display_data && activity.display_data.tenants">
          <strong>Tenants: </strong>
          {{ activity.display_data.tenants.map(tenant => `${tenant.full_name} (${tenant.formatted_phone_number})`).join(', ') }}
        </div>
      </span>

      <template v-for="key in Object.keys(displayableKeys)">
        <span
          v-if="hasField(key)"
          v-html="resolveValueChangeText(key)"
        />
      </template>
    </template>
    <slot />
  </span>
</template>

<script>
import get from 'lodash.get';
import momentUtil from '../utils/MomentUtil';

const displayableKeys = {
  preferred_from: 'schedule date',
  follow_up_at: 'follow up date',
  access_instructions: 'access instructions',
  instructions: 'instructions',
  lock_box_code: 'Lockbox / Keypad Code',
  happy_co_state: 'happy co state',
  agent_payout: 'agent payout',
  price: 'business charge',
  'tenant.first_name': 'first name',
  'tenant.last_name': 'last name',
  'tenant.email': 'email',
  'tenant.phone_number': 'phone',
};
const dateKeys = ['preferred_from', 'follow_up_at'];

export default {
  name: 'InspectionTimelineActivity',
  props: {
    inspection: {
      type: Object,
      required: true,
    },
    activity: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      displayableKeys,
      momentUtil,
    };
  },
  computed: {
    oldValues() {
      return this.activity.old_values;
    },
    newValues() {
      return this.activity.new_values;
    },
    isScheduledByTenant() {
      return this.activity.type === this.$config.inspectionActivityTypes.SCHEDULED_BY_TENANT;
    },
    shouldDisplayCauser() {
      return !this.isScheduledByTenant && this.activity.type !== this.$config.inspectionActivityTypes.TENANT_SCHEDULING_MAIL_SENT;
    },
  },
  methods: {
    hasField(key) {
      return (this.oldValues && this.oldValues[key]) || (this.newValues && this.newValues[key]);
    },
    resolveValueChangeText(lodashPath) {
      const displayableKey = get(displayableKeys, lodashPath);
      let oldValue = get(this.oldValues, lodashPath);
      let newValue = get(this.newValues, lodashPath);
      if (dateKeys.includes(lodashPath)) {
        oldValue = this.formatDateValue(oldValue, this.oldValues.preferred_until);
        newValue = this.formatDateValue(newValue, this.newValues.preferred_until);
      }
      if (oldValue !== null && newValue !== null) {
        return ` changed ${displayableKey} from <strong>${oldValue}</strong> to <strong>${newValue}</strong>`;
      }

      if (oldValue === null) {
        return ` set ${displayableKey} to <strong>${newValue}</strong>`;
      }
      return ` deleted the ${displayableKey} (<strong>${oldValue}</strong>)`;
    },
    formatDateValue(start, end = null) {
      if (start === null) {
        return start;
      }

      let dateValue = momentUtil(start, this.inspection.property.timezone).toDisplayFormat();

      if (end) {
        const endTime = momentUtil(end, this.inspection.property.timezone).toDisplayShortTimeFormat();
        dateValue += ` - ${endTime}`;
      }

      return dateValue;
    },
  },
};

</script>
